<script>
import { Line } from "vue-chartjs";
import ChartAnnotations from "chartjs-plugin-annotation";

const PRIMARY = "#E2782F";

const formatAxisNumber = (value) => {
  if (value >= 1000000) return (value / 1000000).toFixed(1) + "M";
  if (value >= 1000) return (value / 1000).toFixed(0) + "k";
  return value;
};

export default {
  extends: Line,
  props: {
    chartData: { type: Object, required: true },
    startDate: { type: String, default: "" },
  },
  watch: {
    chartData: {
      handler() {
        this.reRenderChart();
      },
      deep: true,
    },
    startDate() {
      this.reRenderChart();
    },
  },
  mounted() {
    this.addPlugin(ChartAnnotations);
    this.reRenderChart();
  },
  methods: {
    buildGradient(height) {
      const canvas = this.$refs.canvas;
      if (!canvas) return "rgba(226, 120, 47, 0.18)";
      const ctx = canvas.getContext("2d");
      const h = height || canvas.clientHeight || 240;
      const gradient = ctx.createLinearGradient(0, 0, 0, h);
      gradient.addColorStop(0, "rgba(226, 120, 47, 0.32)");
      gradient.addColorStop(0.55, "rgba(226, 120, 47, 0.08)");
      gradient.addColorStop(1, "rgba(226, 120, 47, 0)");
      return gradient;
    },
    enhanceDatasets() {
      const gradient = this.buildGradient();
      const datasets = (this.chartData.datasets || []).map((ds, i) => ({
        ...ds,
        backgroundColor: i === 0 ? gradient : ds.backgroundColor,
        fill: i === 0,
        borderColor: ds.borderColor || PRIMARY,
        borderWidth: 2,
        lineTension: 0.35,
        pointRadius: 0,
        pointBorderWidth: 0,
        pointHitRadius: 18,
        pointHoverRadius: 5,
        pointHoverBorderWidth: 2,
        pointHoverBorderColor: "#ffffff",
        pointHoverBackgroundColor: ds.borderColor || PRIMARY,
        spanGaps: true,
      }));
      return {
        labels: this.chartData.labels || [],
        datasets,
      };
    },
    reRenderChart() {
      const data = this.enhanceDatasets();

      this.renderChart(data, {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 16, right: 12, bottom: 4, left: 0 },
        },
        legend: {
          display: false,
        },
        hover: {
          mode: "index",
          intersect: false,
        },
        tooltips: {
          enabled: true,
          mode: "index",
          intersect: false,
          backgroundColor: "rgba(15, 23, 42, 0.94)",
          titleFontColor: "#ffffff",
          titleFontSize: 12,
          titleFontStyle: "600",
          titleMarginBottom: 6,
          bodyFontColor: "rgba(255,255,255,0.82)",
          bodyFontSize: 12,
          bodySpacing: 4,
          xPadding: 12,
          yPadding: 10,
          cornerRadius: 8,
          displayColors: false,
          callbacks: {
            label(tooltipItem, chartData) {
              const label =
                chartData.datasets[tooltipItem.datasetIndex].label || "";
              const n = Number(tooltipItem.yLabel);
              const formatted = Number.isNaN(n)
                ? tooltipItem.yLabel
                : n.toLocaleString();
              return label ? `${label}: ${formatted}` : formatted;
            },
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                autoSkip: true,
                maxTicksLimit: 7,
                maxRotation: 0,
                minRotation: 0,
                fontColor: "rgba(15, 23, 42, 0.48)",
                fontSize: 11,
                padding: 8,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                color: "rgba(15, 23, 42, 0.06)",
                drawBorder: false,
                zeroLineColor: "rgba(15, 23, 42, 0.1)",
              },
              ticks: {
                beginAtZero: true,
                maxTicksLimit: 5,
                fontColor: "rgba(15, 23, 42, 0.48)",
                fontSize: 11,
                padding: 10,
                callback: formatAxisNumber,
              },
            },
          ],
        },
        elements: {
          line: {
            borderJoinStyle: "round",
            borderCapStyle: "round",
          },
        },
        annotation: {
          annotations: this.startDate
            ? [
                {
                  type: "line",
                  mode: "vertical",
                  scaleID: "x-axis-0",
                  value: this.startDate,
                  borderColor: "rgba(15, 23, 42, 0.32)",
                  borderWidth: 1,
                  borderDash: [4, 4],
                  label: {
                    enabled: true,
                    content: "Subscription start",
                    position: "top",
                    backgroundColor: "rgba(15, 23, 42, 0.88)",
                    fontColor: "#ffffff",
                    fontSize: 10,
                    fontStyle: "normal",
                    xPadding: 8,
                    yPadding: 4,
                    cornerRadius: 4,
                    yAdjust: 0,
                  },
                },
              ]
            : [],
        },
      });
    },
  },
};
</script>

<style scoped></style>
